import { Outlet, useRouterState } from '@tanstack/react-router';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { settingsNavigation } from './settingsNavigation';
import { SettingsNav } from './SettingsNav';

/**
 * Einstellungen-Hub-Layout — trennt tägliche Arbeit (Haupt-Sidebar) von Verwaltung,
 * Abrechnung, Integrationen und Diagnose. Linke Spalte = interne Sub-Navigation,
 * rechte Spalte = die bestehende Fachseite (Outlet, unverändertes Routing/URLs).
 *
 * Pathless Layout-Route (`_settings`) — die Kind-Routen behalten ihre absoluten
 * Pfade (/users, /settings/tax, /system/audit …), damit bestehende direkte URLs
 * weiter funktionieren.
 *
 * <p>Unter `lg` ist die Spalte eine Disclosure statt eines gestapelten Blocks (§9, gleiche
 * Ausprägung wie die Ordner-Rail der Akte): bis zu elf Einträge über dem Inhalt hätten auf
 * 360 px bedeutet, dass man vor jeder Einstellungsseite erst an der Navigation vorbeiscrollt.</p>
 */
export function SettingsLayout() {
  const currentPath = useRouterState().location.pathname;
  const [open, setOpen] = useState(false);
  const currentLabel = currentSettingsLabel(currentPath);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-10">
      <aside className="lg:sticky lg:top-6 lg:self-start">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls="settings-bereiche"
          className="border-line bg-card hover:bg-card-alt/60 focus-visible:ring-sage flex min-h-11 w-full items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 lg:hidden"
        >
          <SlidersHorizontal size={17} className="text-ink-soft shrink-0" aria-hidden />
          <span className="text-ink min-w-0 flex-1 truncate font-serif">{currentLabel}</span>
          <ChevronDown
            size={16}
            aria-hidden
            className={cn('text-ink-faint shrink-0 transition-transform', open && 'rotate-180')}
          />
        </button>

        <div
          id="settings-bereiche"
          className={cn(
            'lg:mt-0 lg:block lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0',
            open ? 'border-line bg-card mt-2 rounded-lg border p-2' : 'hidden',
          )}
        >
          <SettingsNav currentPath={currentPath} onNavigate={() => setOpen(false)} />
        </div>
      </aside>
      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

/**
 * Der Auslöser nennt die Seite, auf der man steht — ein reines „Bereiche" verschweigt genau die
 * Auskunft, die auf Mobile sonst niemand hat, weil die Liste eingeklappt ist.
 */
function currentSettingsLabel(currentPath: string): string {
  const match = settingsNavigation
    .flatMap((group) => group.items)
    .find((item) => currentPath === item.to || currentPath.startsWith(`${item.to}/`));
  return match?.label ?? 'Einstellungen';
}
